import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Card family (see DESIGN_SYSTEM.md):
 * - legacy (default): original look, kept so existing pages do not shift
 * - default: neutral GenieBot card, no blur
 * - interactive: small lift + border emphasis
 * - featured: controlled brand-yellow emphasis
 * - category: neutral card with a left accent; set `--card-accent` (e.g. "var(--category-cyber)")
 */
export const cardVariants = {
  legacy: "rounded-lg border bg-card/95 backdrop-blur-md shadow-md transition-all duration-300 hover:shadow-lg hover:border-primary/20",
  default: "rounded-xl border border-border bg-card shadow-sm",
  interactive:
    "rounded-xl border border-border bg-card shadow-sm motion-safe:transition-[transform,box-shadow,border-color] motion-safe:duration-200 hover:border-primary/40 hover:shadow-md motion-safe:hover:-translate-y-0.5",
  featured: "rounded-xl border border-primary/50 bg-card shadow-md ring-1 ring-primary/20",
  category:
    "rounded-xl border border-border border-l-4 [border-left-color:hsl(var(--card-accent,var(--primary)))] bg-card shadow-sm",
} as const;
export type CardVariant = keyof typeof cardVariants;

type CardProps = React.HTMLAttributes<HTMLDivElement> & { variant?: CardVariant };

const Card = React.forwardRef<HTMLDivElement, CardProps>(({ className, variant = "legacy", ...props }, ref) => (
  <div ref={ref} className={cn("focus-ring text-card-foreground", cardVariants[variant], className)} {...props} />
));
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn("text-2xl font-semibold leading-none tracking-tight", className)} {...props} />
  ),
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  ),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />,
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
